import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { FadeIn } from "@/components/FadeIn";
import { generatePreview } from "@/lib/api";

const ROLES = ["Ehefrau", "Ehemann", "Tochter", "Sohn", "Freund:in"];

export default function Preview({ onCta }) {
  const [role, setRole] = useState("");
  const [memory, setMemory] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!role || memory.trim().length < 10) {
      toast.error("Bitte Rolle wählen und einen kurzen Satz schreiben.");
      return;
    }
    setLoading(true);
    try {
      const data = await generatePreview({ role, memory });
      setText(data.text);
    } catch (e) {
      toast.error("Vorschau konnte nicht erzeugt werden. Bitte später erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setText("");
    setMemory("");
  };

  return (
    <section id="preview" className="py-32 md:py-44 bg-parchment/40">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <FadeIn>
          <p className="label-eyebrow mb-6 text-center">Ein Vorgeschmack</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-charcoal text-center leading-[1.1]">
            Spüren Sie, wie es klingen könnte.
          </h2>
          <p className="mt-8 font-serif text-lg text-ink leading-relaxed text-center max-w-xl mx-auto">
            Zwei kurze Antworten — wir zeigen Ihnen die ersten Zeilen, bevor Sie sich entscheiden. Kostenfrei.
          </p>
        </FadeIn>

        {!text ? (
          <FadeIn delay={0.15}>
            <div data-testid="preview-form" className="mt-14 bg-paper rounded-3xl p-8 md:p-12 shadow-card space-y-8">
              <div>
                <p className="font-display tracking-[0.25em] text-[10px] text-umber mb-4">
                  Wer sind Sie?
                </p>
                <div className="flex flex-wrap gap-3">
                  {ROLES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRole(r)}
                      data-testid={`preview-role-${r}`}
                      className={`chip ${role === r ? "chip-selected" : ""}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-display tracking-[0.25em] text-[10px] text-umber mb-4">
                  Eine Erinnerung in einem Satz
                </p>
                <textarea
                  value={memory}
                  onChange={(e) => setMemory(e.target.value)}
                  data-testid="preview-memory"
                  rows={3}
                  placeholder="z. B. Sie hat jeden Sonntag ein Brot gebacken — und das ganze Haus roch danach."
                  className="w-full bg-cream rounded-2xl px-5 py-4 font-serif text-lg text-charcoal
                             placeholder:text-ink/50 outline-none focus:ring-2 focus:ring-umber/40 resize-none leading-relaxed"
                />
              </div>

              <button
                onClick={submit}
                disabled={loading}
                data-testid="preview-submit"
                className="btn-primary w-full disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Ein Moment, bitte…
                  </>
                ) : (
                  "Ersten Atemzug zeigen"
                )}
              </button>

              <p className="font-sans text-xs text-ink/60 text-center">
                Kein Konto, keine Speicherung. Nur ein erster Eindruck.
              </p>
            </div>
          </FadeIn>
        ) : (
          <FadeIn>
            <div data-testid="preview-result" className="mt-14 bg-paper rounded-3xl p-10 md:p-14 shadow-card">
              <p className="font-display tracking-[0.25em] text-[10px] text-umber mb-6">
                Ihre ersten Zeilen
              </p>
              <div className="font-serif italic text-lg md:text-xl text-charcoal leading-[1.85] whitespace-pre-line">
                {text}
              </div>

              <div className="mt-10 pt-10 border-t border-umber/10">
                <p className="font-serif text-lg text-charcoal mb-6 text-center">
                  Möchten Sie den vollständigen Entwurf?
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button onClick={onCta} data-testid="preview-to-pricing" className="btn-primary">
                    29 € · Vollständigen Entwurf erstellen
                  </button>
                  <button onClick={reset} data-testid="preview-reset" className="btn-secondary">
                    Neu beginnen
                  </button>
                </div>
              </div>
            </div>
          </FadeIn>
        )}
      </div>
    </section>
  );
}
